import { errorMessage, type ApiErrorBody, type ErrorCode } from '@nolens/domain';
import { DEFAULT_LOCALE, type Locale } from '@nolens/i18n';
import { config } from './config';
import { miscText } from '../i18n/misc';
import { auth } from './supabase';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode,
    message: string,
    readonly requestId?: string,
    readonly fields?: Record<string, string>,
  ) {
    super(message);
  }
}

export async function authHeader(): Promise<Record<string, string>> {
  const { data } = (await auth?.getSession()) ?? { data: { session: null } };
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Língua dos pedidos (a API responde às mensagens de erro nesta língua). */
let requestLocale: Locale = DEFAULT_LOCALE;
export const setRequestLocale = (l: Locale) => {
  requestLocale = l;
};
export const getRequestLocale = () => requestLocale;

/** Sem rede: a mensagem na língua da interface. */
const offline = () => new ApiError(0, 'INTERNAL', miscText(requestLocale).offline);

async function request(
  path: string,
  init: { method?: string; body?: unknown; anonymous?: boolean } = {},
): Promise<Response> {
  try {
    return await fetch(`${config.apiUrl}${path}`, {
      method: init.method ?? 'GET',
      headers: {
        'Accept-Language': requestLocale,
        ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(init.anonymous ? {} : await authHeader()),
      },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    });
  } catch {
    // status 0 = sem rede
    throw offline();
  }
}

/** Avisa a app (AppShell) de que um limite do plano foi atingido. */
export const PLAN_LIMIT_EVENT = 'nolens:plan-limit';

async function fail(res: Response): Promise<never> {
  const data = (await res.json().catch(() => null)) as ApiErrorBody | null;
  const err = data?.error;
  if (err?.code === 'PLAN_LIMIT')
    window.dispatchEvent(new CustomEvent(PLAN_LIMIT_EVENT, { detail: err.message }));
  throw new ApiError(
    res.status,
    err?.code ?? 'INTERNAL',
    err?.message ?? errorMessage(err?.code ?? 'INTERNAL', requestLocale),
    err?.requestId ?? res.headers.get('X-Request-Id') ?? undefined,
    err?.fields,
  );
}

export async function api<T>(
  path: string,
  init: { method?: string; body?: unknown; anonymous?: boolean } = {},
): Promise<T> {
  const res = await request(path, init);
  if (!res.ok) return fail(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Captura privada: pedida com a sessão e mostrada como blob: (um <img> não envia o token). */
export async function apiBlob(path: string): Promise<Blob> {
  const res = await request(path);
  if (!res.ok) return fail(res);
  return res.blob();
}

export const publicImageUrl = (token: string, imageId: string) =>
  `${config.apiUrl}/public/guides/${token}/images/${imageId}`;

/** Envia um ficheiro (corpo binário) e devolve a resposta JSON. */
export async function apiUpload<T>(path: string, file: Blob): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${config.apiUrl}${path}`, {
      method: 'PUT',
      headers: {
        'Accept-Language': requestLocale,
        'Content-Type': file.type || 'application/octet-stream',
        ...(await authHeader()),
      },
      body: file,
    });
  } catch {
    throw offline();
  }
  if (!res.ok) return fail(res);
  return (await res.json()) as T;
}

/** Formulário com ficheiros (multipart), sem sessão. */
export async function apiForm(path: string, form: FormData): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${config.apiUrl}${path}`, {
      method: 'POST',
      headers: { 'Accept-Language': requestLocale },
      body: form,
    });
  } catch {
    throw offline();
  }
  if (!res.ok) return fail(res);
}

export const publicBrandUrl = (token: string, what: 'logo' | 'font', version: number) =>
  `${config.apiUrl}/public/guides/${token}/brand/${what}?v=${version}`;
